// src/components/maps/POIMarker.jsx
import React from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { Button, Typography, Box } from '@mui/material';
import {
  Place as PlaceIcon,
  Restaurant as RestaurantIcon,
  LocalHospital as HospitalIcon,
  Wc as RestroomIcon,
  ExitToApp as ExitIcon,
  Stairs as StairsIcon,
  Elevator as ElevatorIcon,
  DoorFront as DoorIcon,
  MeetingRoom as RoomIcon,
  LocalParking as ParkingIcon,
  Info as InfoIcon,
  Security as SecurityIcon,
  LocalCafe as CafeIcon,
  Chair as LoungeIcon,
  Accessible as AccessibleIcon,
} from '@mui/icons-material';

/**
 * POI type configuration with icon, color, and label
 */
export const POI_TYPE_CONFIG = {
  office: { icon: PlaceIcon, color: '#2196F3', label: 'Office', svgPath: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z' },
  restaurant: { icon: RestaurantIcon, color: '#FF9800', label: 'Restaurant', svgPath: 'M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z' },
  restroom: { icon: RestroomIcon, color: '#9C27B0', label: 'Restroom', svgPath: 'M5.5 22v-7.5H4V9c0-1.1.9-2 2-2h3c1.1 0 2 .9 2 2v5.5H9.5V22h-4zM18 22v-6h3l-2.54-7.63C18.18 7.55 17.42 7 16.56 7h-.12c-.86 0-1.63.55-1.9 1.37L12 16h3v6h3zM7.5 6c1.11 0 2-.89 2-2s-.89-2-2-2-2 .89-2 2 .89 2 2 2zm9 0c1.11 0 2-.89 2-2s-.89-2-2-2-2 .89-2 2 .89 2 2 2z' },
  exit: { icon: ExitIcon, color: '#F44336', label: 'Exit', svgPath: 'M10.09 15.59L11.5 17l5-5-5-5-1.41 1.41L12.67 11H3v2h9.67l-2.58 2.59zM19 3H5c-1.11 0-2 .9-2 2v4h2V5h14v14H5v-4H3v4c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z' },
  stairs: { icon: StairsIcon, color: '#607D8B', label: 'Stairs', svgPath: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14H6v-2h4v-2H8v-2h4v-2H6V7h6v6h4v4h-4z' },
  elevator: { icon: ElevatorIcon, color: '#009688', label: 'Elevator', svgPath: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-8 12H9V9h2v6zm2-9l3 3h-2v3h-2v-3H10l3-3zm2 9V9h2v6h-2z' },
  entrance: { icon: DoorIcon, color: '#4CAF50', label: 'Entrance', svgPath: 'M19 19V5c0-1.1-.9-2-2-2H7c-1.1 0-2 .9-2 2v14H3v2h18v-2h-2zm-8-6H9v-2h2v2z' },
  medical: { icon: HospitalIcon, color: '#E91E63', label: 'Medical', svgPath: 'M19 3H5c-1.1 0-1.99.9-1.99 2L3 19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 11h-4v4h-4v-4H6v-4h4V6h4v4h4v4z' },
  room: { icon: RoomIcon, color: '#795548', label: 'Room', svgPath: 'M19 19V4h-4V3H5v16H3v2h12V6h2v15h4v-2h-2zm-6 0H7V5h6v14zm-3-8h2v2h-2z' },
  parking: { icon: ParkingIcon, color: '#3F51B5', label: 'Parking', svgPath: 'M13 3H6v18h4v-6h3c3.31 0 6-2.69 6-6s-2.69-6-6-6zm.2 8H10V7h3.2c1.1 0 2 .9 2 2s-.9 2-2 2z' },
  info: { icon: InfoIcon, color: '#00BCD4', label: 'Information', svgPath: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z' },
  security: { icon: SecurityIcon, color: '#FFC107', label: 'Security', svgPath: 'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z' },
  cafe: { icon: CafeIcon, color: '#8D6E63', label: 'Cafe', svgPath: 'M20 3H4v10c0 2.21 1.79 4 4 4h6c2.21 0 4-1.79 4-4v-3h2c1.11 0 2-.89 2-2V5c0-1.11-.89-2-2-2zm0 5h-2V5h2v3zM4 19h16v2H4z' },
  lounge: { icon: LoungeIcon, color: '#CDDC39', label: 'Lounge', svgPath: 'M20 8V6c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v2c-1.1 0-2 .9-2 2v5h1.33L4 18h1l.67-3h12.67l.66 3h1l.67-3H22v-5c0-1.1-.9-2-2-2zm-2-2v2H6V6h12zm-7 8H6v-3h5v3zm2 0v-3h5v3h-5z' },
  accessible: { icon: AccessibleIcon, color: '#2196F3', label: 'Accessible', svgPath: 'M12 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm9 9v-2c-1.54.02-3.09-.75-4.07-1.83l-1.29-1.43c-.17-.19-.38-.34-.61-.45-.01 0-.01-.01-.02-.01H15c-.35-.2-.75-.3-1.19-.26C12.76 7.14 12 8.01 12 9.14V15c0 1.1.9 2 2 2h5v5h2v-5.5c0-1.1-.9-2-2-2h-3v-3.45c1.29 1.07 3.25 1.94 5 1.95zm-9 7c-1.65 0-3-1.35-3-3 0-1.3.84-2.4 2-2.82V12.1c-2.28.46-4 2.48-4 4.9 0 2.76 2.24 5 5 5 2.42 0 4.44-1.72 4.9-4h-2.07c-.41 1.16-1.52 2-2.83 2z' },
};

/**
 * Get icon component for POI type
 */
export const getPOIIcon = (type) => {
  return POI_TYPE_CONFIG[type]?.icon || PlaceIcon;
};

/**
 * Get color for POI type
 */
export const getPOIColor = (type) => {
  return POI_TYPE_CONFIG[type]?.color || '#2196F3';
};

/**
 * Get label for POI type
 */
export const getPOILabel = (type) => {
  return POI_TYPE_CONFIG[type]?.label || 'Location';
};

/**
 * Create custom Leaflet icon for POI with actual symbol
 */
const createPOIIcon = (type) => {
  const config = POI_TYPE_CONFIG[type] || POI_TYPE_CONFIG.office;
  const color = config.color;
  const svgPath = config.svgPath;

  return new L.DivIcon({
    className: 'poi-marker',
    html: `
      <div style="
        width: 36px;
        height: 36px;
        background: ${color};
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 3px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
          <path d="${svgPath}"/>
        </svg>
      </div>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -18],
  });
};

/**
 * Get POI type key from POI data
 * Maps POI type name/category from API to our config keys
 */
const getPOITypeKey = (poi) => {
  // First check for direct type code
  if (poi?.type && POI_TYPE_CONFIG[poi.type]) {
    return poi.type;
  }

  // Try to get from poiType relation (API response)
  const poiTypeName = poi?.poiType?.name?.toLowerCase() || '';
  const poiTypeIcon = poi?.poiType?.icon?.toLowerCase() || '';
  const poiTypeCategory = poi?.poiType?.category?.toLowerCase() || '';

  // Map common POI type names to our config keys
  const nameMapping = {
    // Medical/Safety
    'aed': 'medical',
    'defibrillator': 'medical',
    'first aid': 'medical',
    'medical': 'medical',
    'keyvault': 'security',
    'key vault': 'security',
    'security': 'security',
    'fire extinguisher': 'exit',
    'emergency exit': 'exit',
    'exit': 'exit',
    // Navigation
    'entrance': 'entrance',
    'main entrance': 'entrance',
    'elevator': 'elevator',
    'lift': 'elevator',
    'stairs': 'stairs',
    'staircase': 'stairs',
    // Facilities
    'restroom': 'restroom',
    'toilet': 'restroom',
    'wc': 'restroom',
    'bathroom': 'restroom',
    'restaurant': 'restaurant',
    'cafeteria': 'restaurant',
    'cafe': 'cafe',
    'coffee': 'cafe',
    'lounge': 'lounge',
    'waiting room': 'lounge',
    'parking': 'parking',
    'garage': 'parking',
    // General
    'office': 'office',
    'room': 'room',
    'meeting room': 'room',
    'conference': 'room',
    'information': 'info',
    'info': 'info',
    'reception': 'info',
    'accessible': 'accessible',
    'wheelchair': 'accessible',
  };

  // Try to match by name
  for (const [key, value] of Object.entries(nameMapping)) {
    if (poiTypeName.includes(key)) {
      return value;
    }
  }

  // Try to match by icon field
  for (const [key, value] of Object.entries(nameMapping)) {
    if (poiTypeIcon.includes(key)) {
      return value;
    }
  }

  // Fall back to category mapping
  const categoryMapping = {
    'safety': 'exit',
    'medical': 'medical',
    'facility': 'restroom',
    'navigation': 'entrance',
    'service': 'info',
    'other': 'office',
  };

  if (categoryMapping[poiTypeCategory]) {
    return categoryMapping[poiTypeCategory];
  }

  // Default to office
  return 'office';
};

/**
 * POI marker component
 * @param {object} poi - POI data with geo_lat/geo_lng (geographic coordinates)
 * @param {function} onClick - Click handler
 * @param {object} floorPlan - Optional floor plan for pixel-to-geo conversion
 */
const POIMarker = ({ poi, onClick, floorPlan }) => {
  // Get POI type key for styling
  const poiType = getPOITypeKey(poi);

  // Determine coordinates - prefer geo coordinates
  let poiLat = poi?.geo_lat;
  let poiLng = poi?.geo_lng;

  // If geo coords are missing but we have pixel coords and floor plan bounds, convert
  if ((poiLat === undefined || poiLat === null) && poi?.y_position !== undefined && floorPlan) {
    const bounds = floorPlan.bounds_north && floorPlan.bounds_south ? {
      north: parseFloat(floorPlan.bounds_north),
      south: parseFloat(floorPlan.bounds_south),
      east: parseFloat(floorPlan.bounds_east),
      west: parseFloat(floorPlan.bounds_west),
    } : floorPlan.bounds;

    if (bounds && floorPlan.image_width && floorPlan.image_height) {
      const xRatio = poi.x_position / floorPlan.image_width;
      const yRatio = poi.y_position / floorPlan.image_height;
      poiLat = bounds.north - yRatio * (bounds.north - bounds.south);
      poiLng = bounds.west + xRatio * (bounds.east - bounds.west);
    }
  }

  // Parse string coordinates to numbers
  if (typeof poiLat === 'string') poiLat = parseFloat(poiLat);
  if (typeof poiLng === 'string') poiLng = parseFloat(poiLng);

  if (!poi || poiLat === undefined || poiLng === undefined || isNaN(poiLat) || isNaN(poiLng)) {
    console.warn('[POIMarker] POI missing valid coordinates:', poi);
    return null;
  }

  const icon = createPOIIcon(poiType);
  const IconComponent = getPOIIcon(poiType);

  return (
    <Marker
      position={[poiLat, poiLng]}
      icon={icon}
    >
      <Popup>
        <Box sx={{ p: 1, minWidth: 200 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <IconComponent
              sx={{
                color: getPOIColor(poiType),
                mr: 1,
              }}
            />
            <Typography variant="subtitle1" fontWeight="bold">
              {poi.name}
            </Typography>
          </Box>

          {poi.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {poi.description}
            </Typography>
          )}

          {onClick && (
            <Button
              variant="contained"
              fullWidth
              size="small"
              onClick={() => onClick(poi)}
            >
              Navigate Here
            </Button>
          )}
        </Box>
      </Popup>
    </Marker>
  );
};

export default POIMarker;
export { getPOITypeKey };
